import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, MessageSquare, Send, Smile } from "lucide-react";
import api, { WS_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useJobsite } from "../context/JobsiteContext";
import UserAvatar from "../components/UserAvatar";

const QUICK = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
const PICKER = [
  "😀", "😂", "🤣", "😊", "😍", "😎", "🤔",
  "😅", "😢", "😡", "👍", "👎", "👏", "🙌",
  "🙏", "💪", "🔥", "✅", "❌", "⚠️", "🚧",
  "🏗️", "🦺", "🔧", "⏰", "📍", "📷", "📝",
];

const NAME_TONES = [
  "text-orange-300",
  "text-sky-300",
  "text-emerald-300",
  "text-violet-300",
  "text-rose-300",
  "text-amber-300",
];

function hashOf(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

function sameDay(a, b) {
  return a.toDateString() === b.toDateString();
}

function dayLabel(d) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

function timeLabel(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function Chat() {
  const { user } = useAuth();
  const { activeSite } = useJobsite();
  const siteId = activeSite?.id || "";

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [connected, setConnected] = useState(false);
  const [showJump, setShowJump] = useState(false);

  const listRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const nearBottomRef = useRef(true);
  const readRef = useRef(new Set());
  const isTouch = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches,
    []
  );

  /* ---------- carregar e receber mensagens ---------- */

  const myId = user?.id;
  const markRead = useCallback(
    (list) => {
      if (!myId) return;
      list
        .filter((m) => m.user_id !== myId && !(m.read_by || []).includes(myId) && !readRef.current.has(m.id))
        .slice(-30)
        .forEach((m) => {
          readRef.current.add(m.id);
          api.post(`/chat/${m.id}/read`).catch(() => {});
        });
    },
    [myId]
  );

  const loadMessages = useCallback(
    async (silent) => {
      if (!siteId) return;
      try {
        const { data } = await api.get(`/chat/${siteId}/messages`, { params: { limit: 200 } });
        const list = Array.isArray(data) ? data : [];
        setMessages(list);
        markRead(list);
      } catch (err) {
        if (!silent) toast.error("Could not load the chat.");
      } finally {
        setLoading(false);
      }
    },
    [siteId, markRead]
  );

  useEffect(() => {
    setMessages([]);
    setLoading(true);
    nearBottomRef.current = true;
    loadMessages(false);
  }, [loadMessages]);

  const addMessage = useCallback(
    (m) => {
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      markRead([m]);
    },
    [markRead]
  );

  // Tempo real (websocket) com reconexao automatica.
  useEffect(() => {
    if (!siteId) return undefined;
    let ws = null;
    let closed = false;
    let retry = 0;
    let timer = null;

    const connect = () => {
      const token = localStorage.getItem("ss_token");
      if (!token || closed) return;
      ws = new WebSocket(`${WS_BASE}/api/ws/chat/${siteId}?token=${encodeURIComponent(token)}`);
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        loadMessages(true);
      };
      ws.onmessage = (ev) => {
        try {
          const evt = JSON.parse(ev.data);
          if (evt.type === "message") {
            addMessage(evt.data);
          } else if (evt.type === "reaction") {
            const d = evt.data;
            setMessages((prev) => prev.map((m) => (m.id === d.id ? { ...m, reactions: d.reactions } : m)));
          }
        } catch (e) {
          // mensagem de tempo real que nao e nossa: ignora
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (!closed) {
          retry += 1;
          timer = setTimeout(connect, Math.min(10000, 1000 * 2 ** retry));
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {
          // ja estava fechado
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      if (ws) ws.close();
    };
  }, [siteId, addMessage, loadMessages]);

  // Se o tempo real cair, busca de novo de vez em quando.
  useEffect(() => {
    if (connected || !siteId) return undefined;
    const t = setInterval(() => loadMessages(true), 15000);
    return () => clearInterval(t);
  }, [connected, siteId, loadMessages]);

  /* ---------- rolagem ---------- */

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    nearBottomRef.current = distance < 120;
    setShowJump(distance > 300);
  };

  const scrollToBottom = (smooth) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "end" });
  };

  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (nearBottomRef.current || last.user_id === user?.id) scrollToBottom(false);
  }, [messages, user]);

  /* ---------- enviar e reagir ---------- */

  const resizeInput = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const send = async () => {
    const t = text.trim();
    if (!t || sending || !siteId) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/send", { jobsite_id: siteId, text: t, mentions: [] });
      nearBottomRef.current = true;
      addMessage({ ...data, avatar_url: data.avatar_url || user?.avatar_url });
      setText("");
      setShowEmoji(false);
      requestAnimationFrame(resizeInput);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the message.");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isTouch) {
      e.preventDefault();
      send();
    }
  };

  const react = async (msg, emoji) => {
    setActiveId(null);
    const uid = user?.id;
    if (!uid) return;
    // Mostra na hora; o servidor confirma em seguida.
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msg.id) return m;
        const reactions = { ...(m.reactions || {}) };
        const people = [...(reactions[emoji] || [])];
        const i = people.indexOf(uid);
        if (i >= 0) people.splice(i, 1);
        else people.push(uid);
        if (people.length) reactions[emoji] = people;
        else delete reactions[emoji];
        return { ...m, reactions };
      })
    );
    try {
      await api.post(`/chat/${msg.id}/react`, { emoji });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not react to the message.");
      loadMessages(true);
    }
  };

  /* ---------- lista agrupada por dia e por pessoa ---------- */

  const rows = useMemo(() => {
    const out = [];
    let lastDay = null;
    let prev = null;
    messages.forEach((m) => {
      const d = new Date(m.created_at);
      if (!lastDay || !sameDay(lastDay, d)) {
        out.push({ type: "day", key: `day-${d.toDateString()}`, label: dayLabel(d) });
        lastDay = d;
        prev = null;
      }
      const grouped = prev && prev.user_id === m.user_id && d - new Date(prev.created_at) < 5 * 60 * 1000;
      out.push({ type: "msg", key: m.id, m, first: !grouped });
      prev = m;
    });
    return out;
  }, [messages]);

  /* ---------- tela ---------- */

  if (!siteId) {
    return (
      <div className="grid h-[60vh] place-items-center px-6 text-center">
        <div>
          <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground" />
          <div className="mt-3 text-lg font-semibold">Pick a jobsite</div>
          <div className="mt-1 text-sm text-muted-foreground">Choose a jobsite at the top to open its chat.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh_-_8rem_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom))] flex-col md:h-[calc(100dvh_-_4rem)]">
      {/* Topo do chat */}
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-4 py-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
          <MessageSquare className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold leading-tight">{activeSite?.name}</div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500" : "bg-amber-500"}`} />
            {connected ? "Live team chat" : "Reconnecting..."}
          </div>
        </div>
      </div>

      {/* Mensagens */}
      <div className="relative flex-1 overflow-hidden">
        <div ref={listRef} onScroll={onScroll} onClick={() => setActiveId(null)} className="h-full overflow-y-auto px-3 py-4">
          {loading && <div className="py-10 text-center text-sm text-muted-foreground">Loading messages...</div>}

          {!loading && messages.length === 0 && (
            <div className="grid place-items-center py-16 text-center">
              <div className="text-4xl">👋</div>
              <div className="mt-3 text-lg font-semibold">No messages yet</div>
              <div className="mt-1 text-sm text-muted-foreground">Say hello to your team.</div>
            </div>
          )}

          {rows.map((row) => {
            if (row.type === "day") {
              return (
                <div key={row.key} className="my-4 flex justify-center">
                  <span className="rounded-full border border-border bg-card px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {row.label}
                  </span>
                </div>
              );
            }

            const m = row.m;
            const mine = m.user_id === user?.id;
            const reactions = Object.entries(m.reactions || {})
              .filter(([, people]) => people && people.length > 0)
              .sort((a, b) => b[1].length - a[1].length);
            const open = activeId === m.id;
            const staff = m.role === "admin" || m.role === "supervisor";

            return (
              <div key={row.key} className={`flex gap-2 ${mine ? "justify-end" : "justify-start"} ${row.first ? "mt-3" : "mt-0.5"}`}>
                {!mine && (
                  <div className="w-9 shrink-0">
                    {row.first && <UserAvatar url={m.avatar_url} name={m.user_name} size={36} />}
                  </div>
                )}

                <div className={`relative flex max-w-[78%] flex-col ${mine ? "items-end" : "items-start"}`}>
                  {open && (
                    <div
                      className={`absolute -top-12 z-20 flex gap-0.5 rounded-full border border-border bg-card px-2 py-1 shadow-xl ${
                        mine ? "right-0" : "left-0"
                      }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {QUICK.map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => react(m, e)}
                          className="grid h-10 w-10 place-items-center rounded-full text-2xl transition-transform active:scale-125"
                          aria-label={`React ${e}`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  )}

                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveId(open ? null : m.id);
                    }}
                    className={`cursor-pointer rounded-2xl px-3 py-2 ${
                      mine
                        ? "rounded-tr-md bg-primary text-black"
                        : "rounded-tl-md border border-border bg-card text-foreground"
                    }`}
                  >
                    {!mine && row.first && (
                      <div className="mb-0.5 flex items-center gap-1.5">
                        <span className={`text-[13px] font-semibold ${NAME_TONES[hashOf(m.user_id || m.user_name || "") % NAME_TONES.length]}`}>
                          {m.user_name}
                        </span>
                        {staff && (
                          <span className="rounded border border-border px-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                            {m.role}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words text-[15px] leading-snug">{m.text}</div>
                    <div className={`mt-1 text-right font-mono text-[10px] ${mine ? "text-black/60" : "text-muted-foreground"}`}>
                      {timeLabel(m.created_at)}
                    </div>
                  </div>

                  {reactions.length > 0 && (
                    <div className={`-mt-1.5 flex flex-wrap gap-1 ${mine ? "justify-end pr-2" : "justify-start pl-2"}`}>
                      {reactions.map(([emoji, people]) => {
                        const iReacted = people.includes(user?.id);
                        return (
                          <button
                            key={emoji}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              react(m, emoji);
                            }}
                            className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm shadow-sm ${
                              iReacted ? "border-primary/60 bg-primary/20" : "border-border bg-card"
                            }`}
                          >
                            <span>{emoji}</span>
                            {people.length > 1 && <span className="font-mono text-[11px]">{people.length}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {showJump && (
          <button
            type="button"
            onClick={() => scrollToBottom(true)}
            aria-label="Go to latest message"
            className="absolute bottom-3 right-3 grid h-11 w-11 place-items-center rounded-full border border-border bg-card shadow-lg active:bg-accent"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Emojis para escrever */}
      {showEmoji && (
        <div className="grid grid-cols-7 gap-1 border-t border-border bg-card px-3 py-2">
          {PICKER.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                setText((t) => t + e);
                inputRef.current?.focus();
              }}
              className="grid h-11 place-items-center rounded-md text-2xl active:bg-accent"
            >
              {e}
            </button>
          ))}
        </div>
      )}

      {/* Escrever */}
      <div className="border-t border-border bg-card px-3 py-2">
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => setShowEmoji((v) => !v)}
            onPointerDown={(e) => e.preventDefault()}
            aria-label="Emojis"
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-full active:bg-accent ${
              showEmoji ? "text-primary" : "text-muted-foreground"
            }`}
          >
            <Smile className="h-6 w-6" />
          </button>
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              resizeInput();
            }}
            onKeyDown={onKeyDown}
            placeholder="Message"
            className="max-h-[120px] min-h-[44px] flex-1 resize-none rounded-3xl border border-border bg-background px-4 py-[10px] text-base leading-snug outline-none focus:border-primary"
            data-testid="chat-input"
          />
          <button
            type="button"
            onClick={send}
            onPointerDown={(e) => e.preventDefault()}
            disabled={!text.trim() || sending}
            aria-label="Send"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary text-black transition-opacity disabled:opacity-40"
            data-testid="chat-send"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
